import { useEffect, useMemo, useRef, useState } from 'react';
import { UploadCloud, Loader2, RefreshCw, FileDown, Check } from 'lucide-react';
import type { EkLineMapping, EkPositionsResult, ExtractionJob } from '@/types';
import { bauvorhabenApi, dokumenteApi } from '@/services/api';
import { emitAppEvent } from '@/utils/appEvents';
import { formatCurrency, formatDateTime } from '@/utils/currency';

const STATUS_LABELS: Record<ExtractionJob['status'] | 'nicht_gestartet', string> = {
  nicht_gestartet: 'Nicht gestartet',
  pending: 'Wartet auf Worker',
  processing: 'In Bearbeitung',
  done: 'Fertig',
  failed: 'Fehlgeschlagen',
};

/** Clientseitiges Auto-Match-Vorbild des Servers (gleiche Normalisierung:
 *  lowercase, Umlaute nach DIN 5008-2, Whitespace kollabiert). */
function normalizeName(v: string): string {
  return v
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();
}

function suggestMatch(name: string, catalog: { id: number; name: string }[]): number | null {
  const q = normalizeName(name);
  if (!q) return null;
  const exact = catalog.filter((g) => normalizeName(g.name) === q);
  if (exact.length === 1) return exact[0].id;
  const sub = catalog.filter((g) => {
    const n = normalizeName(g.name);
    return n.includes(q) || q.includes(n);
  });
  return sub.length === 1 ? sub[0].id : null;
}

function apiError(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}

interface BauvorhabenEinkaufExtraktionCardProps {
  bauvorhabenId: number;
  /** Alle Extraktions-Jobs des BVs (Liste, ohne Ergebnis) — null = Fehler. */
  jobs: ExtractionJob[] | null;
  refreshJobs: () => void;
  /** Review/Übernehmen: nur Finanzen/Admin (Vertrieb-Kurve = readonly). */
  canApply: boolean;
  /** Uploads/Extraktion des Eigen-BV als Vertrieb (keine Zeilen/Summen sichtbar). */
  vertriebMode?: boolean;
  catalogGewerke: { id: number; name: string }[];
  /** Nach Übernehmen: Einkauf-Zeilen nachladen. */
  onApplied: () => void;
}

/** Netto-EK-PDF → 'ek_positionen'-Extraktion (Phase 3). Upload (Kategorie
 *  'abrechnung') direkt in der Karte, Job-Start, 4-s-Polling, Review-Mapping
 *  pro Zeile (Vorschlag/MATCH/Freitext/Überspringen), Übernehmen nur Finanzen.
 *  Vertrieb sieht nur Upload + Status — niemals Zeilen oder Summen (maske
 *  auch clientseitig, Server gated das zusätzlich). */
export function BauvorhabenEinkaufExtraktionCard({
  bauvorhabenId, jobs, refreshJobs, canApply, vertriebMode = false, catalogGewerke, onApplied,
}: BauvorhabenEinkaufExtraktionCardProps) {
  const ekJobs = useMemo(
    () => (jobs ?? []).filter((j) => j.job_type === 'ek_positionen'),
    [jobs],
  );
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [jobDetail, setJobDetail] = useState<ExtractionJob | null>(null);
  const [detailError, setDetailError] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [pollCapped, setPollCapped] = useState(false);

  // Upload + Start
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Review-Mapping (Index → Mapping-Zeile)
  const [mapping, setMapping] = useState<Record<string, EkLineMapping>>({});
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [applyInfo, setApplyInfo] = useState('');

  const result = vertriebMode
    ? null
    : (jobDetail?.result as unknown as EkPositionsResult | null | undefined);
  const pollWorthy = !!jobDetail && (jobDetail.status === 'pending' || jobDetail.status === 'processing');

  // Job laden (Status + Ergebnis)
  useEffect(() => {
    if (selectedJobId === null) {
      setJobDetail(null);
      return;
    }
    let cancelled = false;
    setDetailError('');
    bauvorhabenApi
      .getExtraction(bauvorhabenId, selectedJobId)
      .then((response) => {
        if (!cancelled) setJobDetail(response.data.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setJobDetail(null);
          setDetailError(apiError(err, 'Extraktion konnte nicht geladen werden.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [bauvorhabenId, selectedJobId]);

  // Poll: offen (pending/processing) → 4 s Takt, Cap 10 Minuten
  useEffect(() => {
    if (!pollWorthy) {
      setIsPolling(false);
      return;
    }
    setIsPolling(true);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt >= 10 * 60 * 1000) {
        setPollCapped(true);
        setIsPolling(false);
        clearInterval(timer);
        return;
      }
      bauvorhabenApi
        .getExtraction(bauvorhabenId, jobDetail!.id)
        .then((response) => setJobDetail(response.data.data))
        .catch(() => {
          // einmaliger Poll-Fehler: nächster Versuch folgt
        });
    }, 4000);
    return () => clearInterval(timer);
  }, [pollWorthy, bauvorhabenId, jobDetail?.id]);

  // Mapping-Vorschlag initialisieren, sobald ein Ergebnis ankommt
  const resultKey = result ? result.gewerke.map((r) => r.nummer + ':' + r.gesamt_netto).join('|') : '';
  useEffect(() => {
    if (!result) {
      setMapping({});
      return;
    }
    const initial: Record<string, EkLineMapping> = {};
    result.gewerke.forEach((row, index) => {
      const gewerkId = suggestMatch(row.name, catalogGewerke);
      initial[String(index)] = gewerkId !== null ? { mode: 'match', gewerk_id: gewerkId } : { mode: 'freetext' };
    });
    setMapping(initial);
    setApplyError('');
    setApplyInfo('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultKey]);

  const uploadAndStart = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadError('');
    setApplyInfo('');
    try {
      const uploadResponse = await dokumenteApi.upload({
        bauvorhaben_id: bauvorhabenId,
        category: 'abrechnung',
        title: 'Netto-EK-PDF',
        files: [selectedFile],
      });
      const documentId = uploadResponse.data.data.documents[0].id;
      const startResponse = await bauvorhabenApi.startExtraction(
        bauvorhabenId, documentId, 'ek_positionen',
      );
      const job = startResponse.data.data;
      refreshJobs();
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setSelectedJobId(job.id);
      setPollCapped(false);
      emitAppEvent('dokumente:changed');
    } catch (err) {
      setUploadError(apiError(err, 'Upload oder Extraktions-Start fehlgeschlagen.'));
    } finally {
      setIsUploading(false);
    }
  };

  const apply = async () => {
    if (!jobDetail || !result) return;
    // 'match' ohne gewerk_id blockieren (Server wäre ein Validierungs-Fehler)
    const missingMatch = Object.entries(mapping).some(
      ([index, m]) => m.mode === 'match' && !m.gewerk_id && result.gewerke[Number(index)] !== undefined,
    );
    if (missingMatch) {
      setApplyError('Katalog-Gewerk wählen — Zeilen mit „Budget setzen" brauchen ein Ziel.');
      return;
    }
    setIsApplying(true);
    setApplyError('');
    setApplyInfo('');
    try {
      const response = await bauvorhabenApi.applyExtractionEk(bauvorhabenId, jobDetail.id, { mapping });
      const { applied, skipped } = response.data.data;
      setApplyInfo(`Übernommen: ${applied} Budget(s) gesetzt${skipped.length > 0 ? `, ${skipped.length} übersprungen` : ''}.`);
      emitAppEvent('einkauf:changed');
      onApplied();
      refreshJobs();
    } catch (err) {
      setApplyError(apiError(err, 'Übernehmen fehlgeschlagen.'));
    } finally {
      setIsApplying(false);
    }
  };

  const setLineMode = (index: string, mode: EkLineMapping['mode']) => {
    setMapping((prev) => ({
      ...prev,
      [index]: mode === 'match' && prev[index]?.gewerk_id
        ? { mode, gewerk_id: prev[index].gewerk_id }
        : { mode },
    }));
  };
  const setLineGewerk = (index: string, gewerkId: number) => {
    setMapping((prev) => ({ ...prev, [index]: { mode: 'match', gewerk_id: gewerkId } }));
  };

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileDown className="h-5 w-5 text-gray-400" />
          Netto-EK-PDF (Extraktion)
        </h2>
        {!vertriebMode && ekJobs.length > 0 && (
          <select
            className="input max-w-xs text-sm"
            value={selectedJobId ?? ''}
            onChange={(e) => setSelectedJobId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">Extraktion auswählen…</option>
            {ekJobs.map((job) => (
              <option key={job.id} value={job.id}>
                #{job.id} · {job.original_name ?? job.document_title ?? 'PDF'} · {STATUS_LABELS[job.status]}
              </option>
            ))}
          </select>
        )}
      </div>

      {vertriebMode ? (
        <p className="mb-4 text-xs text-gray-500">
          Netto-EK-PDF hochladen und Extraktion anstoßen — die Auswertung
          übernimmt die Rolle Finanzen nach dem GPS-Handover.
        </p>
      ) : (
        <p className="mb-4 text-xs text-gray-500">
          Seite „Summenübersicht Gewerke" wird geparst (deterministisch, LLM-Fallback nur auf dieser Seite).
          Nach der Extraktion Zeilen prüfen, auf Katalog-Gewerke mappen und übernehmen —
          Budget überschreibt bestehende Werte (jederzeit korrigierbar in der EK-Tabelle).
        </p>
      )}

      {/* Upload + Start */}
      <div className="mb-4 rounded-lg border border-dashed border-gray-300 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="text-sm"
            onChange={(e) => {
              setSelectedFile(e.target.files?.[0] ?? null);
              setUploadError('');
            }}
          />
          <button
            type="button"
            className="btn btn-primary inline-flex items-center gap-2 text-sm"
            disabled={!selectedFile || isUploading}
            onClick={uploadAndStart}
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {isUploading ? 'Lädt hoch…' : 'Hochladen + Extraktion starten'}
          </button>
          {isPolling && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Status wird alle 4 s abgefragt
            </span>
          )}
        </div>
        {uploadError && (
          <p className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{uploadError}</p>
        )}
      </div>

      {detailError && (
        <p className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{detailError}</p>
      )}

      {!vertriebMode && selectedJobId !== null && jobDetail && (
        <>
          {/* Status-Zeile */}
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-gray-700">Job #{jobDetail.id}</span>
            <span className={`rounded px-2 py-0.5 text-xs ${
              jobDetail.status === 'done' ? 'bg-green-100 text-green-800'
                : jobDetail.status === 'failed' ? 'bg-red-100 text-red-800'
                : jobDetail.status === 'processing' || jobDetail.status === 'pending' ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {STATUS_LABELS[jobDetail.status]}
            </span>
            {jobDetail.finished_at && (
              <span className="text-xs text-gray-500">Fertig: {formatDateTime(jobDetail.finished_at)}</span>
            )}
            {jobDetail.applied_at && (
              <span className="text-xs text-green-700">Übernommen</span>
            )}
            <button type="button" onClick={() => { setSelectedJobId(jobDetail.id); setPollCapped(false); }} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Aktualisieren
            </button>
          </div>
          {jobDetail.error_message && (
            <p className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {jobDetail.error_message}
            </p>
          )}
          {pollCapped && (
            <p className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Polling nach 10 Minuten angehalten — manuell aktualisieren.
            </p>
          )}

          {isPolling && (
            <p className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Worker verarbeitet die Datei (Status: {STATUS_LABELS[jobDetail.status]})…
            </p>
          )}

          {/* Review-Tabelle */}
          {result && result.gewerke && (
            <div className="overflow-x-auto">
              {result.source_meta?.parser && (
                <p className="mb-2 text-xs text-gray-500 flex items-center gap-1">
                  <Check className="h-3 w-3 text-green-600" />
                  Deterministisch geparst ({result.gewerke.length} Gewerke · Gesamt {formatCurrency(result.gesamtsumme_netto)})
                </p>
              )}
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                    <th className="py-2 pr-3 font-medium">Nr.</th>
                    <th className="py-2 pr-3 font-medium">Gewerk (aus PDF)</th>
                    <th className="py-2 pr-3 font-medium text-right">Lst./Lohn netto</th>
                    <th className="py-2 pr-3 font-medium text-right">Material netto</th>
                    <th className="py-2 pr-3 font-medium text-right">Ges. EK netto</th>
                    <th className="py-2 pr-3 font-medium">Übernahme</th>
                    <th className="py-2 font-medium">Katalog-Gewerk</th>
                  </tr>
                </thead>
                <tbody>
                  {result.gewerke.map((row, index) => {
                    const m = mapping[String(index)] ?? { mode: 'freetext' };
                    const suggested = suggestMatch(row.name, catalogGewerke);
                    const suggestedName = suggested !== null
                      ? catalogGewerke.find((g) => g.id === suggested)?.name
                      : null;
                    return (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2 pr-3 text-xs text-gray-600">{row.nummer}</td>
                        <td className="py-2 pr-3 font-medium text-gray-900">{row.name}</td>
                        <td className="py-2 pr-3 text-right">{formatCurrency(row.lst_netto)}</td>
                        <td className="py-2 pr-3 text-right">{formatCurrency(row.material_netto)}</td>
                        <td className="py-2 pr-3 text-right font-medium">{formatCurrency(row.gesamt_netto)}</td>
                        <td className="py-2 pr-3">
                          {canApply ? (
                            <select
                              className="input text-xs"
                              value={m.mode}
                              onChange={(e) => setLineMode(String(index), e.target.value as EkLineMapping['mode'])}
                            >
                              {suggested !== null && (
                                <option value="match">Budget setzen — {suggestedName} (Vorschlag)</option>
                              )}
                              <option value="freetext">Neue Freitext-Zeile</option>
                              <option value="skip">Überspringen</option>
                            </select>
                          ) : (
                            <span className="text-xs text-gray-500">
                              {m.mode === 'skip' ? 'Überspringen' : m.mode === 'freetext' ? 'Freitext' : 'Budget setzen'}
                            </span>
                          )}
                        </td>
                        <td className="py-2">
                          {m.mode === 'match' && canApply ? (
                            <select
                              className="input text-xs"
                              value={m.gewerk_id ?? ''}
                              onChange={(e) => setLineGewerk(String(index), Number(e.target.value))}
                            >
                              <option value="">Katalog-Gewerk wählen…</option>
                              {catalogGewerke.map((g) => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {m.mode === 'match' && !canApply && suggestedName ? suggestedName : '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {applyError && (
                <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{applyError}</p>
              )}
              {applyInfo && (
                <p className="mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{applyInfo}</p>
              )}

              {canApply && (
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isApplying}
                    onClick={apply}
                  >
                    {isApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Übernehmen
                  </button>
                  <span className="text-xs text-gray-500">
                    Budget überschreibt bestehende Werte (KI-Kennzeichen bleibt sichtbar).
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Vertrieb-Detailbox: nur Upload + Start — kein Review, keine Zeilen. */
export function EinkaufVertriebUploadBox({
  bauvorhabenId, jobs, refreshJobs, catalogGewerke, onApplied,
}: Omit<BauvorhabenEinkaufExtraktionCardProps, 'canApply'>) {
  return (
    <BauvorhabenEinkaufExtraktionCard
      bauvorhabenId={bauvorhabenId}
      jobs={jobs}
      refreshJobs={refreshJobs}
      canApply={false}
      vertriebMode
      catalogGewerke={catalogGewerke}
      onApplied={onApplied}
    />
  );
}